import { useMemo, useState } from 'react'
import { useNavigate, useParams } from '@tanstack/react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Button } from '../ui/button'
import { Alert, AlertDescription } from '../ui/alert'
import { RelationTypeFormFields } from './RelationTypeFormFields'
import { relationTypesQueryKey } from './RelationTypesPage'
import {
    emptyRelationTypeForm,
    formToCreateRequest,
    isValidRelationTypeCode,
    type AssetRelationType,
    type RelationTypeFormState,
} from './relation-type-form'
import { apiClient } from '../../api/client'
import { apiErrorMessage } from '../../lib/apiError'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { PageHeader } from '../shared/PageHeader'
import { SECTION_ICONS } from '../../lib/section-icons'

export function CreateRelationTypePage() {
    const { org } = useParams({ strict: false }) as { org?: string }
    const orgCode = org || 'demo'
    const { t } = useTranslation('types')
    const navigate = useNavigate()
    const queryClient = useQueryClient()

    const [form, setForm] = useState<RelationTypeFormState>(() => emptyRelationTypeForm())
    const [pageError, setPageError] = useState<string | null>(null)

    useBreadcrumbs(
        useMemo(
            () => [
                { title: t('breadcrumb.home'), path: `/${orgCode}` },
                { title: t('breadcrumb.org_settings'), path: `/${orgCode}/settings` },
                { title: t('relation_types.title'), path: `/${orgCode}/relation-types` },
                { title: t('relation_types.form.create_title') },
            ],
            [orgCode, t]
        )
    )

    const createMutation = useMutation({
        mutationFn: async () => {
            const response = await apiClient.POST('/api/v1/organizations/{orgCode}/asset-relation-types', {
                params: { path: { orgCode } },
                body: formToCreateRequest(form),
            })
            if (response.error) {
                throw new Error(apiErrorMessage(response.error, 'Failed to create relation type'))
            }
            return response.data as AssetRelationType
        },
        onSuccess: created => {
            queryClient.invalidateQueries({ queryKey: relationTypesQueryKey(orgCode) })
            navigate({
                to: '/$org/relation-types/$relationTypeUid',
                params: { org: orgCode, relationTypeUid: created.uid },
            })
        },
        onError: (e: Error) => setPageError(`${t('relation_types.form.create_error')} ${e.message}`),
    })

    const canSubmit =
        isValidRelationTypeCode(form.code) &&
        Object.values(form.name).some(v => v && v.trim()) &&
        !createMutation.isPending

    return (
        <div className="container mx-auto max-w-4xl py-8">
            <div className="rounded-lg bg-card shadow-lg">
                <div className="border-b border-border px-6 py-4">
                    <PageHeader
                        icon={SECTION_ICONS.relationTypes}
                        title={t('relation_types.form.create_title')}
                    />
                </div>

                <div className="space-y-6 p-6">
                    {pageError && (
                        <Alert variant="destructive" data-testid="relation-type-page-error">
                            <AlertDescription>{pageError}</AlertDescription>
                        </Alert>
                    )}

                    <RelationTypeFormFields
                        org={orgCode}
                        value={form}
                        onChange={setForm}
                        codeEditable
                        disabled={createMutation.isPending}
                    />

                    <div className="flex justify-end gap-2 border-t border-border pt-4">
                        <Button
                            variant="outline"
                            onClick={() =>
                                navigate({ to: '/$org/relation-types', params: { org: orgCode } })
                            }
                            disabled={createMutation.isPending}
                            data-testid="relation-type-cancel-btn"
                        >
                            {t('relation_types.form.cancel')}
                        </Button>
                        <Button
                            onClick={() => {
                                setPageError(null)
                                createMutation.mutate()
                            }}
                            disabled={!canSubmit}
                            data-testid="relation-type-save-btn"
                        >
                            {createMutation.isPending
                                ? t('relation_types.form.creating')
                                : t('relation_types.form.create_submit')}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    )
}
